import {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Entity} from '../../backend/interfaces/entity';
import {AuthContext, useAuth} from '../../contexts/auth/context';
import {AuthContextType} from '../../contexts/auth/types';
import {OfficeContext} from '../../contexts/office/context';
import {OfficeContextType} from '../../contexts/office/types';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {translations} from '../../i18n/translations';
import {EntitiesRepository} from '../../repositories/entities';
import {EntityForm} from '../entity-form/entity-form';
import {CaregiverMode} from '../../enums';

export const AffiliatedEntityProfile = (props: {
  entityId: string;
  redirectUrl: string;
  canWrite: boolean;
}) => {
  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const {
    state: {
      session: {
        selectedAffiliation: {contractsOverview: {includesDigitalInclusion = false} = {}} = {},
      } = {},
    } = {},
  } = useAuth();

  // Translations
  const {t} = useTranslation();

  // Get navigation info
  let history = useHistory();

  // Validate and get the entity
  useEffect(() => {
    const getEntity = async () => {
      if (
        props.entityId &&
        authContext.state.session?.affiliations.find((obj) => obj.entity.id === props.entityId)
      ) {
        try {
          // Get entity
          const entityDetails: Entity = await EntitiesRepository.instance.getById(props.entityId);

          if (entityDetails) {
            // Set entity for edit or view
            officeContext.setEntityForm(entityDetails);

            officeContext.setEntityFormLoadState(false);
          } else {
            // Error
            throw new Error('Empty entity.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get entity.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.affiliated_entity_profile.errors.entity_does_not_exist_error),
          });

          // Redirect
          history.push(props.redirectUrl);
        }
      } else {
        // Log
        console.debug(
          '🔴 Could not get entity because the id was not set or the user is not affiliated with the entity.'
        );

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.affiliated_entity_profile.errors.entity_does_not_exist_error),
        });

        // Redirect
        history.push(props.redirectUrl);
      }
    };

    // Get entity
    getEntity();

    // Clean up
    return () => {
      // Clear entity
      officeContext.setEntityFormLoadState(true);

      officeContext.clearEntityForm();

      // Log
      console.debug('🧹 Entity form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.entityId]);

  return (
    <EntityForm
      redirectUrl={props.redirectUrl}
      showExitButton
      showPageTitle
      caregiverMode={CaregiverMode.Professional}
      canWrite={props.canWrite}
      hasDIaccess={includesDigitalInclusion}
    />
  );
};
